import * as THREE from "three";
import { COLOR_HEX, type Color } from "@core/colors";
import { loadLevels, getLevel, nextLevelIndex, levelNumber } from "@core/level-loader";
import { levels as levelData } from "./levels";

const canvas = document.getElementById("game") as HTMLCanvasElement;
const hudLevel = document.getElementById("hud-level")!;
const hudStatus = document.getElementById("hud-status")!;
const banner = document.getElementById("banner")!;

const BOX_CAPACITY = 3;

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x141027);

const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
camera.position.set(0, 0.6, 7);
camera.lookAt(0, 0, 0);

scene.add(new THREE.AmbientLight(0xffffff, 0.55));
const sun = new THREE.DirectionalLight(0xffffff, 0.9);
sun.position.set(5, 6, 4);
scene.add(sun);

// HUD overlay scene (orthographic) for boxes
const hudScene = new THREE.Scene();
const hudCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
hudCamera.position.z = 10;

interface BoxState {
  color: Color;
  group: THREE.Group;
  fillMesh: THREE.Mesh;
  pieces: number;
}

interface RingHandle {
  color: Color;
  mesh: THREE.Mesh; // ring (torus)
  pin: THREE.Mesh; // small cylinder underneath
  released: boolean;
}

let model: THREE.Group | null = null;
let rings: RingHandle[] = [];
let boxes: BoxState[] = [];
let currentLevelIdx = 0;
const allLevels = loadLevels(levelData);

function applySize() {
  const parent = canvas.parentElement!;
  const w = parent.clientWidth;
  const h = parent.clientHeight;
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  // HUD ortho frustum matches screen units
  const aspect = w / h;
  const heightOrtho = 10;
  hudCamera.left = (-heightOrtho * aspect) / 2;
  hudCamera.right = (heightOrtho * aspect) / 2;
  hudCamera.top = heightOrtho / 2;
  hudCamera.bottom = -heightOrtho / 2;
  hudCamera.updateProjectionMatrix();
}
applySize();
window.addEventListener("resize", applySize);

function makeRoundedRectMesh(width: number, height: number, radius: number, color: number) {
  const shape = new THREE.Shape();
  const x = -width / 2;
  const y = -height / 2;
  shape.moveTo(x + radius, y);
  shape.lineTo(x + width - radius, y);
  shape.quadraticCurveTo(x + width, y, x + width, y + radius);
  shape.lineTo(x + width, y + height - radius);
  shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  shape.lineTo(x + radius, y + height);
  shape.quadraticCurveTo(x, y + height, x, y + height - radius);
  shape.lineTo(x, y + radius);
  shape.quadraticCurveTo(x, y, x + radius, y);
  return new THREE.Mesh(
    new THREE.ShapeGeometry(shape),
    new THREE.MeshBasicMaterial({ color }),
  );
}

function buildBoxesHud(palette: Color[]) {
  for (const b of boxes) hudScene.remove(b.group);
  boxes = [];

  const w = 1.3;
  const gap = 0.3;
  const total = palette.length * w + (palette.length - 1) * gap;
  const startX = -total / 2 + w / 2;
  const y = 3.6;
  for (let i = 0; i < palette.length; i++) {
    const color = palette[i];
    const group = new THREE.Group();
    group.position.set(startX + i * (w + gap), y, 0);
    const back = makeRoundedRectMesh(w, w, 0.22, 0x2a1f3a);
    group.add(back);
    const rim = makeRoundedRectMesh(w * 0.92, w * 0.28, 0.1, COLOR_HEX[color]);
    rim.position.set(0, w * 0.32, 0.05);
    group.add(rim);
    const fill = makeRoundedRectMesh(w * 0.84, 0.02, 0.04, COLOR_HEX[color]);
    fill.position.set(0, -w * 0.36, 0.06);
    fill.scale.y = 0.001;
    group.add(fill);
    hudScene.add(group);
    boxes.push({ color, group, fillMesh: fill, pieces: 0 });
  }
}

function clearLevel() {
  if (model) {
    scene.remove(model);
    model = null;
  }
  rings = [];
}

function spawnLevel(levelIdx: number) {
  clearLevel();

  const cfg = getLevel(allLevels, levelIdx);
  buildBoxesHud(cfg.palette);

  model = new THREE.Group();
  scene.add(model);

  // Base "model" — a soft rounded cube
  const baseGeom = new THREE.BoxGeometry(2.2, 2.2, 2.2, 4, 4, 4);
  const baseMat = new THREE.MeshStandardMaterial({
    color: 0xffd7e6,
    roughness: 0.55,
    metalness: 0.05,
  });
  const base = new THREE.Mesh(baseGeom, baseMat);
  model.add(base);

  for (const spec of cfg.rings) {
    const { pos, normal } = facePoint(spec.face, spec.u, spec.v);
    const ring = makeRingOnSurface(spec.color, pos, normal);
    model.add(ring.mesh, ring.pin);
    rings.push(ring);
  }

  hudLevel.textContent = `Level ${levelNumber(levelIdx)}`;
  hudStatus.textContent = "Tap rings · swipe to rotate";
}

function facePoint(face: "x+" | "x-" | "y+" | "y-" | "z+" | "z-", u: number, v: number): {
  pos: THREE.Vector3;
  normal: THREE.Vector3;
} {
  const half = 1.1;
  // u, v range roughly -0.95..0.95 maps across the face
  const su = u * half * 0.95;
  const sv = v * half * 0.95;
  switch (face) {
    case "z+":
      return { pos: new THREE.Vector3(su, sv, half + 0.05), normal: new THREE.Vector3(0, 0, 1) };
    case "z-":
      return { pos: new THREE.Vector3(su, sv, -half - 0.05), normal: new THREE.Vector3(0, 0, -1) };
    case "x+":
      return { pos: new THREE.Vector3(half + 0.05, sv, su), normal: new THREE.Vector3(1, 0, 0) };
    case "x-":
      return { pos: new THREE.Vector3(-half - 0.05, sv, su), normal: new THREE.Vector3(-1, 0, 0) };
    case "y+":
      return { pos: new THREE.Vector3(su, half + 0.05, sv), normal: new THREE.Vector3(0, 1, 0) };
    case "y-":
      return { pos: new THREE.Vector3(su, -half - 0.05, sv), normal: new THREE.Vector3(0, -1, 0) };
  }
}

function makeRingOnSurface(color: Color, pos: THREE.Vector3, normal: THREE.Vector3): RingHandle {
  const torus = new THREE.TorusGeometry(0.28, 0.07, 12, 24);
  const mat = new THREE.MeshStandardMaterial({
    color: COLOR_HEX[color],
    roughness: 0.4,
    metalness: 0.1,
  });
  const ring = new THREE.Mesh(torus, mat);
  ring.position.copy(pos);
  // Orient ring perpendicular to face normal (so its disc faces along the normal)
  const quat = new THREE.Quaternion();
  quat.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
  ring.quaternion.copy(quat);

  const pinGeom = new THREE.CylinderGeometry(0.08, 0.08, 0.18, 12);
  const pinMat = new THREE.MeshStandardMaterial({
    color: 0xece6ff,
    roughness: 0.3,
    metalness: 0.4,
  });
  const pin = new THREE.Mesh(pinGeom, pinMat);
  pin.position.copy(pos);
  pin.position.addScaledVector(normal, 0.07);
  // Cylinder default axis is Y; rotate so it aligns with normal
  pin.quaternion.copy(quat);
  pin.rotateX(Math.PI / 2);

  return { color, mesh: ring, pin, released: false };
}

// --- input: swipe to rotate, tap to pick ---
let dragging = false;
let lastX = 0;
let lastY = 0;
let dragMoved = false;
const DRAG_THRESHOLD = 6;

canvas.addEventListener("pointerdown", (ev: PointerEvent) => {
  dragging = true;
  dragMoved = false;
  lastX = ev.clientX;
  lastY = ev.clientY;
});

canvas.addEventListener("pointermove", (ev: PointerEvent) => {
  if (!dragging) return;
  const dx = ev.clientX - lastX;
  const dy = ev.clientY - lastY;
  if (Math.hypot(dx, dy) > DRAG_THRESHOLD) dragMoved = true;
  if (model) {
    model.rotation.y += dx * 0.01;
    model.rotation.x += dy * 0.008;
    model.rotation.x = Math.max(-Math.PI / 2.2, Math.min(Math.PI / 2.2, model.rotation.x));
  }
  lastX = ev.clientX;
  lastY = ev.clientY;
});

canvas.addEventListener("pointerup", (ev: PointerEvent) => {
  dragging = false;
  if (dragMoved) return;
  pickAt(ev);
});

canvas.addEventListener("pointercancel", () => {
  dragging = false;
});

const raycaster = new THREE.Raycaster();
const ndc = new THREE.Vector2();

function pickAt(ev: PointerEvent) {
  const rect = canvas.getBoundingClientRect();
  ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -(((ev.clientY - rect.top) / rect.height) * 2 - 1);
  raycaster.setFromCamera(ndc, camera);
  const candidates = rings.filter((r) => !r.released).map((r) => r.mesh);
  const hits = raycaster.intersectObjects(candidates, false);
  if (hits.length === 0) return;
  const ring = rings.find((r) => r.mesh === hits[0].object);
  if (!ring) return;
  releaseRing(ring);
}

function releaseRing(ring: RingHandle) {
  ring.released = true;
  // Find target box
  const found = boxes.find((b) => b.color === ring.color && b.pieces < BOX_CAPACITY);
  if (!found) {
    hudStatus.textContent = "No box for this color!";
    ring.released = false;
    return;
  }
  const targetBox: BoxState = found;

  // Animate ring out: pop it off the surface, then fly into the HUD box.
  // We need to convert HUD ortho coords → world coords on the perspective scene to interpolate.
  // For V0 we simplify: translate the ring outward along its normal, then fade.
  const outward = ring.mesh.position.clone().normalize().multiplyScalar(2);
  const start = performance.now();
  const startPos = ring.mesh.position.clone();
  const dur = 480;
  function step(now: number) {
    const t = Math.min(1, (now - start) / dur);
    const e = 1 - Math.pow(1 - t, 3);
    ring.mesh.position.lerpVectors(startPos, outward, e);
    (ring.mesh.material as THREE.MeshStandardMaterial).opacity = 1 - e;
    (ring.mesh.material as THREE.MeshStandardMaterial).transparent = true;
    if (t < 1) requestAnimationFrame(step);
    else {
      if (model) {
        model.remove(ring.mesh);
        model.remove(ring.pin);
      }
      targetBox.pieces++;
      targetBox.fillMesh.scale.y = Math.max(
        0.001,
        (targetBox.pieces / BOX_CAPACITY) * 28,
      );
      if (targetBox.pieces >= BOX_CAPACITY) {
        flyAwayBox(targetBox);
      }
      maybeWin();
    }
  }
  requestAnimationFrame(step);
}

function flyAwayBox(b: BoxState) {
  const start = performance.now();
  const startY = b.group.position.y;
  const dur = 460;
  function step(now: number) {
    const t = Math.min(1, (now - start) / dur);
    const e = 1 - Math.pow(1 - t, 3);
    b.group.position.y = startY + e * 4;
    b.group.scale.setScalar(1 - e * 0.7);
    if (t < 1) requestAnimationFrame(step);
    else hudScene.remove(b.group);
  }
  requestAnimationFrame(step);
}

function maybeWin() {
  if (rings.every((r) => r.released)) {
    showBanner(`Level ${levelNumber(currentLevelIdx)} clear!`, 1700);
    setTimeout(() => {
      currentLevelIdx = nextLevelIndex(allLevels, currentLevelIdx);
      spawnLevel(currentLevelIdx);
    }, 1700);
  }
}

function showBanner(text: string, durationMs: number) {
  banner.textContent = text;
  banner.classList.add("show");
  setTimeout(() => banner.classList.remove("show"), durationMs);
}

spawnLevel(currentLevelIdx);

function loop() {
  requestAnimationFrame(loop);
  if (model) {
    model.rotation.y += 0.001; // gentle idle spin
  }
  renderer.autoClear = true;
  renderer.render(scene, camera);
  renderer.autoClear = false;
  renderer.render(hudScene, hudCamera);
}
loop();
